import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProfileProvider, useProfile } from "@/context/ProfileContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { Waves } from "lucide-react";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import AddActivity from "@/pages/AddActivity";
import EditActivity from "@/pages/EditActivity";
import MotionCardPage from "@/pages/MotionCardPage";
import ProgressPage from "@/pages/ProgressPage";
import ProfilePage from "@/pages/ProfilePage";
import CommunityPage from "@/pages/CommunityPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import DeleteDataPage from "@/pages/legal/DeleteDataPage";

const Splash = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4">
    <div className="h-14 w-14 rounded-2xl bg-cyan-300/15 border border-cyan-300/30 flex items-center justify-center animate-pulse">
      <Waves className="text-cyan-300" size={26} />
    </div>
    <p className="text-sm text-[hsl(var(--muted-foreground))]">Blue Ocean Motion</p>
  </div>
);

const RequireProfile = ({ children }) => {
  const { profile, initializing } = useProfile();
  if (initializing) return <Splash />;
  if (!profile) return <Navigate to="/onboarding" replace />;
  return children;
};

const OnboardingGate = () => {
  const { profile, initializing } = useProfile();
  if (initializing) return <Splash />;
  if (profile) return <Navigate to="/" replace />;
  return <Onboarding />;
};

function App() {
  return (
    // notranslate + translate="no": protect against browser auto-translation;
    // language is handled by the internal i18n dictionaries only.
    <div className="App min-h-screen notranslate" translate="no">
      <LanguageProvider>
        <ProfileProvider>
          <BrowserRouter>
            <Routes>
              {/* Phase 9: PUBLIC legal routes — no profile required, never
                  gated, never redirected to Dashboard or Onboarding. */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/delete-data" element={<DeleteDataPage />} />
              <Route path="/delete-account" element={<DeleteDataPage />} />

              <Route path="/onboarding" element={<OnboardingGate />} />
              <Route
                path="/motion-card"
                element={
                  <RequireProfile>
                    <MotionCardPage />
                  </RequireProfile>
                }
              />
              <Route
                element={
                  <RequireProfile>
                    <AppShell />
                  </RequireProfile>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/add" element={<AddActivity />} />
                <Route path="/edit-activity/:id" element={<EditActivity />} />
                <Route path="/progress" element={<ProgressPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </ProfileProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
